// Kontekst do zarządzania listą produktów do porównania

import React, { createContext, useContext, useState, useEffect, FC, ReactNode } from 'react';

const STORAGE_KEY = 'compare_products';

interface ICompareProduct {
  id: number;
  title?: string;
  image?: string;
}

interface ICompareContext {
  compareProducts: ICompareProduct[];
  addToCompare: (product: ICompareProduct) => void;
  removeFromCompare: (productId: number) => void;
  clearCompare: () => void;
  isInCompare: (productId: number) => boolean;
}

const CompareContext = createContext<ICompareContext | undefined>(undefined);

export const CompareProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [compareProducts, setCompareProducts] = useState<ICompareProduct[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(compareProducts));
  }, [compareProducts]);

  const addToCompare = (product: ICompareProduct) => {
    setCompareProducts((prev) => {
      if (prev.find((p) => p.id === product.id)) return prev;
      return [...prev, product];
    });
  };

  const removeFromCompare = (productId: number) => {
    setCompareProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const clearCompare = () => setCompareProducts([]);

  const isInCompare = (productId: number) =>
    compareProducts.some((p) => p.id === productId);

  return (
    <CompareContext.Provider
      value={{ compareProducts, addToCompare, removeFromCompare, clearCompare, isInCompare }}>
      {children}
    </CompareContext.Provider>
  );
};

export const useCompare = (): ICompareContext => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used within CompareProvider');
  return ctx;
};